import Globe from '@apps-common/react-globe';
import DialogTitle from '@apps-common/ui-components/DialogTitle.jsx';
import CodeIcon from '@mui/icons-material/Code';
import PublicIcon from '@mui/icons-material/Public';
import ShareIcon from '@mui/icons-material/Share';
import {
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  Link,
  TextField,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {format, parseISO} from 'date-fns';
import React, {Fragment, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useParams} from 'react-router';
import {getStory} from '../../api/stories.js';
import BYIcon from '../../assets/icons/by.jsx';
import CCIcon from '../../assets/icons/cc.jsx';
import NCIcon from '../../assets/icons/nc.jsx';
import {ASSETS_URL, COMPARE_URL, EXPLORER_URL} from '../../config.js';
import {
  ASPECT_RATIO,
  PENDING_STATUSES,
  STORY_WIDTH,
  VIEWER_EVENT,
} from '../../constants.js';
import {latLonToString} from '../../util/lat-lon.js';
import NotFound from '../not-found.jsx';
import Compare from './compare.jsx';
import ShareDialog from './share-dialog/index.jsx';
import Video from './video.jsx';

const Loading = styled('div')({
  margin: 'auto',
  textAlign: 'center',
});

const LoadingText = styled(props => (
  <Typography color="inherit" variant="subtitle1" {...props} />
))(({theme}) => ({
  marginTop: theme.spacing(),
}));

const Media = styled('div')(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: theme.palette.common.black,
}));

const Pending = styled('div')({
  width: '100%',
  maxWidth: STORY_WIDTH,
  position: 'relative',
  '&::before': {
    content: "''",
    display: 'block',
    paddingTop: `${(1 / ASPECT_RATIO) * 100}%`,
  },
});

const PendingInner = styled('div')(({theme}) => ({
  color: theme.palette.grey[100],
  textAlign: 'center',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
}));

const Container = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  width: '100%',
  maxWidth: theme.breakpoints.values.md,
  margin: '0 auto',
  padding: theme.spacing(3),
  backgroundColor: (theme.vars || theme).palette.background.paper,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const Title = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(),
  canvas: {
    display: 'block',
  },
}));

const TitleText = styled('div')(({theme}) => ({
  margin: 'auto 0',
  marginLeft: theme.spacing(),
}));

const HiddenOnMobile = styled('span')(({theme}) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const Subheading = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: (theme.vars || theme).palette.text.secondary,
}));

const SubheadingButton = styled(props => <Button color="inherit" {...props} />)(
  ({theme}) => ({
    borderRadius: 0,
    '& svg': {
      marginRight: theme.spacing(1),
    },
  }),
);

const Legal = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 'auto',
  color: (theme.vars || theme).palette.text.secondary,
}));

const License = styled(Link)(({theme}) => ({
  display: 'flex',
  marginLeft: theme.spacing(1),
  '& svg': {
    width: 20,
    height: 20,
    fill: 'currentColor',
    '&:not(:last-child)': {
      marginRight: theme.spacing(0.75),
    },
  },
}));

const Viewer = () => {
  const {storyId} = useParams();
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [hasOverflow, setHasOverfow] = useState(false);
  const [refetchInterval, setRefetchInterval] = useState(2000);

  const theme = useTheme();

  const {data: story, isLoading: storyLoading} = useQuery({
    queryKey: ['story', storyId],
    queryFn: () => getStory(storyId),
    refetchInterval,
  });

  useEffect(() => {
    if (story && !PENDING_STATUSES.includes(story.status)) {
      setRefetchInterval(0);
    }
  }, [story]);
  const trackEvent = action =>
    analytics.track(VIEWER_EVENT, {
      ...story,
      action,
    });

  const isEllipsisActive = element => {
    if (element) {
      const checkOverflow =
        element.offsetHeight < element.scrollHeight ||
        element.offsetWidth < element.scrollWidth;
      setHasOverfow(checkOverflow);
    }
  };

  const onShareClick = () => {
    setShareDialogOpen(true);
    trackEvent('Share button clicked');
  };

  const onEmbedClick = () => {
    setEmbedDialogOpen(true);
    trackEvent('Embed button clicked');
  };

  const onEmbedFieldClick = event => {
    event.target.focus();
    event.target.select();
  };

  const onExplorerClick = () => trackEvent('View in Explorer button clicked');

  const closeShareDialog = () => {
    setShareDialogOpen(false);
  };

  const closeEmbedDialog = () => {
    setEmbedDialogOpen(false);
  };

  const getEmbedCode = () => {
    const width = STORY_WIDTH;
    const height = width / ASPECT_RATIO;
    switch (story.format) {
      case 'mp4': {
        const url = `${ASSETS_URL}/${story.id}/movie.mp4`;
        return `<video width="${width}" height="${height}" src="${url}" />`;
      }
      case 'raw': {
        const url = `${COMPARE_URL}/?id=${story.id}`;
        return `<iframe width="${width}" height="${height}" src="${url}" />`;
      }
      default:
        return null;
    }
  };

  const renderMedia = () => {
    const {status} = story;
    if (status !== 'Complete') {
      return (
        <Pending>
          <PendingInner>
            <CircularProgress />
            <LoadingText>
              {status}
              ...
            </LoadingText>
          </PendingInner>
        </Pending>
      );
    }

    switch (story.format) {
      case 'mp4':
        return <Video story={story} trackEvent={trackEvent} />;
      case 'raw':
        return <Compare story={story} />;
      default:
        return null;
    }
  };

  if (!story) {
    if (storyLoading) {
      return (
        <Loading>
          <CircularProgress />
          <LoadingText>Loading...</LoadingText>
        </Loading>
      );
    }
    return <NotFound />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>{story.title}</title>
      </Helmet>

      <Media>{renderMedia()}</Media>

      <Container>
        <Title>
          <Tooltip
            title={latLonToString([story.center[1], story.center[0]])}
            data-cy="story-coordinates-globe"
          >
            <div>
              <Globe
                diameter={56}
                center={story.center}
                landColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[300]
                }
                oceanColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : theme.palette.grey[100]
                }
                markerColor={theme.palette.primary.main}
              />
            </div>
          </Tooltip>
          <TitleText sx={{width: '100%'}}>
            <Typography
              variant="h5"
              data-cy="story-title"
              noWrap
              title={story.title}
            >
              {story.title}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              data-cy="story-author"
            >
              by {story.author}
            </Typography>
          </TitleText>
        </Title>

        <Subheading>
          <Typography variant="body1" color="inherit" data-cy="story-date">
            <HiddenOnMobile>Published on </HiddenOnMobile>
            {format(parseISO(story.created), 'MMM d, yyyy')}
          </Typography>

          <div>
            <SubheadingButton
              onClick={onShareClick}
              data-cy="story-share-button"
            >
              <ShareIcon />
              Share
            </SubheadingButton>

            <SubheadingButton
              onClick={onEmbedClick}
              data-cy="story-embed-button"
            >
              <CodeIcon />
              Embed
            </SubheadingButton>

            <HiddenOnMobile>
              <SubheadingButton
                target="_blank"
                href={`${EXPLORER_URL}/#/center/${story.center}/zoom/${story.zoom}`}
                onClick={onExplorerClick}
                data-cy="story-explore-button"
              >
                <PublicIcon />
                Explore
              </SubheadingButton>
            </HiddenOnMobile>
          </div>
        </Subheading>

        <Divider />

        {story.description && (
          <Collapse orientation="vertical" collapsedSize={80} in={showMore}>
            <Typography
              variant="body2"
              sx={{
                width: '100%',
                wordWrap: 'break-word',
                marginBottom: 2,
              }}
              noWrap={!showMore}
              data-cy="story-description"
              ref={isEllipsisActive}
            >
              {story.description}
            </Typography>
            <Button
              size="mini"
              onClick={() => {
                setShowMore(!showMore);
              }}
              disabled={!hasOverflow}
            >
              Show
              {showMore && hasOverflow ? ' Less' : ' More'}
            </Button>
          </Collapse>
        )}
        <Legal>
          <Typography
            color="inherit"
            variant="caption"
            data-cy="story-disclaimer"
          >
            The views expressed here do not reflect those of{' '}
            <Link
              color="inherit"
              variant="inherit"
              underline="hover"
              href="https://www.planet.com"
              target="_blank"
              data-cy="story-disclaimer-url"
            >
              Planet Labs PBC
            </Link>
          </Typography>
          <License
            color="inherit"
            href="https://creativecommons.org/licenses/by-nc/3.0/"
            target="_blank"
            title="Shared under CC BY-NC 3.0 US"
            data-cy="story-license"
            underline="none"
          >
            <CCIcon />
            <BYIcon />
            <NCIcon />
          </License>
        </Legal>
      </Container>
      <ShareDialog
        open={shareDialogOpen}
        onClose={closeShareDialog}
        story={story}
        trackEvent={trackEvent}
      />
      <Dialog fullWidth open={embedDialogOpen} onClose={closeEmbedDialog}>
        <DialogTitle
          data-cy="popup-title"
          title="Embed story"
          onClose={closeEmbedDialog}
        />
        <DialogContent>
          <DialogContentText paragraph>
            Copy and paste this embed code
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            readOnly
            value={getEmbedCode()}
            onClick={onEmbedFieldClick}
            data-cy="popup-description"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeEmbedDialog}
            data-cy="embed-done"
            color="primary"
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default Viewer;
